import { useState, useEffect, useRef } from 'react';
import { Form } from 'antd';
import { FolderOutlined, FolderOpenOutlined, FileOutlined } from '@ant-design/icons';
import './Horizontal.scss';

const renderDescription = (leafNode) => {
  const configDefinition = leafNode?.configDefinition;
  return configDefinition ? (
    <div className="description selected">
      <p><strong>Name:</strong> {configDefinition.name}</p>
      <p><strong>Version:</strong> {configDefinition.version}</p>
      <p><strong>Description:</strong> {configDefinition.description}</p>
    </div>
  ) : (
    <div className="description">
      <p className="placeholder">Select a functionality to see its description.</p>
    </div>
  );
};

const Horizontal = ({ treeData, onLeafNodeSelect, form }) => {
  const [selectedPath, setSelectedPath] = useState([]);
  const [leafNode, setLeafNode] = useState(null);
  const prevLeafNodeRef = useRef(null);
  const [expandedNodesMap, setExpandedNodesMap] = useState({});

  const getNodesAtLevel = (nodes, levelPath) => {
    return levelPath.reduce((currentNodes, name) => {
      const selectedNode = currentNodes.find((node) => node.name === name);
      return selectedNode?.children ?? [];
    }, nodes);
  };

  const getPathFromNode = (nodes, targetName, path = []) => {
    for (const node of nodes) {
      const newPath = [...path, node.name];

      if (node.name === targetName || node.configDefinition?.name === targetName) {
        return newPath;
      }

      if (node.children?.length) {
        const foundPath = getPathFromNode(node.children, targetName, newPath);
        if (foundPath.length) return foundPath;
      }
    }
    return [];
  };

  const selectNode = (level, nodeName) => {
    setSelectedPath((prev) => {
      const newPath = [...prev.slice(0, level), nodeName];
      const nodesAtCurrentLevel = getNodesAtLevel(treeData, newPath.slice(0, -1));
      const selectedNode = nodesAtCurrentLevel.find((node) => node.name === nodeName);

      const isLeaf = selectedNode && (!selectedNode.children || selectedNode.children.length === 0);
      setLeafNode(isLeaf ? selectedNode : null);
      if (isLeaf && onLeafNodeSelect) {
        onLeafNodeSelect(selectedNode);
      }

      setExpandedNodesMap((prevExpanded) => {
        const newExpanded = { ...prevExpanded };
        newExpanded[level] = nodeName;
        return newExpanded;
      });

      return newPath;
    });
  };

  useEffect(() => {
    if (leafNode && prevLeafNodeRef.current !== leafNode) {
      prevLeafNodeRef.current = leafNode;
      form.setFieldsValue({ functionality: leafNode });
    }
  }, [leafNode, form]);

  useEffect(() => {
    const functionality = form.getFieldValue('functionality');
    if (functionality && !prevLeafNodeRef.current) {
      const path = getPathFromNode(treeData, functionality.configDefinition?.name);
      if (path.length) {
        setSelectedPath(path);
        setLeafNode(functionality);

        setExpandedNodesMap(
          path.reduce((acc, nodeName, index) => ({ ...acc, [index]: nodeName }), {})
        );
      }
    }
  }, [form, treeData]);

  return (
    <>
      <Form.Item name="functionality" noStyle />
      <Form.Item label="Functionality">
        <div className="functionality-tree-container">
          <div className="tree">
            {[treeData, ...selectedPath].reduce(
              (columns, _, level) => {
                const currentPath = selectedPath.slice(0, level);
                const nodes = getNodesAtLevel(treeData, currentPath);
                if (nodes.length) {
                  columns.push(
                    <div key={level} className="column">
                      {nodes.map((node) => (
                        <div
                          key={node.name}
                          className={`node ${selectedPath[level] === node.name ? 'selected' : ''}`}
                          onClick={() => selectNode(level, node.name)}
                        >
                          <span className="icon">
                            {node.children?.length ? (
                              expandedNodesMap[level] === node.name ? <FolderOpenOutlined /> : <FolderOutlined />
                            ) : (
                              <FileOutlined />
                            )}
                          </span>
                          <span>{node.name}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
                return columns;
              },
              []
            )}
          </div>
          {renderDescription(leafNode)}
        </div>
      </Form.Item>
    </>
  );
};

export default Horizontal;
